/**
 * Validation des entrées du domaine devis.
 *
 * Les schémas vivent dans le noyau, pas dans le front : c'est ce qui garantit
 * qu'un devis créé par l'agent Telegram subit exactement les mêmes règles
 * qu'un devis saisi à l'écran. Un schéma dupliqué côté interface serait un
 * second jeu de règles à maintenir, donc un jeu de règles qui diverge.
 */
import { z } from "zod";

export const quoteLineInput = z.object({
  id: z.string().optional(),
  isSectionHeader: z.boolean().default(false),
  sectionTitle: z.string().nullish(),
  designation: z.string().min(1, "Description requise"),
  unit: z.string().default("u"),
  quantity: z.coerce.number().min(0, "La quantité ne peut pas être négative"),
  unitCost: z.coerce.number().min(0, "Le coût ne peut pas être négatif").default(0),
  unitPrice: z.coerce.number().min(0, "Le prix ne peut pas être négatif"),
  tvaRate: z.coerce.number().min(0).max(50).default(20),
  notes: z.string().nullish(),
});

export const saveQuoteInput = z.object({
  /** Absent = création, présent = mise à jour. */
  quoteId: z.string().uuid().optional(),
  confirmationId: z.string().optional(),

  clientId: z.string().uuid("Choisissez un client"),
  title: z.string().min(3, "Le titre est trop court"),
  description: z.string().nullish(),

  siteAddressLine1: z.string().nullish(),
  sitePostalCode: z
    .string()
    .regex(/^\d{5}$/, "Le code postal doit faire 5 chiffres")
    .nullish()
    .or(z.literal("").transform(() => null)),
  siteCity: z.string().nullish(),

  validityDays: z.coerce.number().int().min(1).max(365).default(30),
  estimatedDurationDays: z.coerce.number().int().min(0).nullish(),

  // TVA réduite : travaux sur logement achevé depuis plus de deux ans. Le
  // taux applicable engage l'entreprise en cas de contrôle, d'où l'attestation.
  appliesReducedTva: z.boolean().default(false),
  reducedTvaRate: z.coerce.number().min(0).max(50).default(10),
  attestationTva: z.string().nullish(),

  paymentConditions: z.string().nullish(),
  warrantyText: z.string().nullish(),
  specialConditions: z.string().nullish(),
  observations: z.string().nullish(),

  lines: z.array(quoteLineInput).min(1, "Un devis doit comporter au moins une ligne"),
});

export type SaveQuoteInput = z.infer<typeof saveQuoteInput>;

export const listQuotesInput = z.object({
  confirmationId: z.string().optional(),
  status: z
    .array(
      z.enum([
        "draft",
        "sent",
        "viewed",
        "accepted",
        "rejected",
        "expired",
        "cancelled",
      ]),
    )
    .optional(),
  clientId: z.string().uuid().optional(),
  expiredOnly: z.boolean().optional(),
});

export type ListQuotesInput = z.infer<typeof listQuotesInput>;

export const readQuoteInput = z.object({
  confirmationId: z.string().optional(),
  quoteId: z.string().uuid(),
});

export type ReadQuoteInput = z.infer<typeof readQuoteInput>;

export const changeQuoteStatusInput = z.object({
  confirmationId: z.string().optional(),
  quoteId: z.string().uuid(),
  status: z.enum(["draft", "viewed", "accepted", "rejected", "cancelled"]),
  /** Motif du refus. Sans valeur légale, mais c'est la donnée qui permet de
   *  savoir si on perd sur le prix ou sur les délais. */
  rejectionReason: z.string().nullish(),
});

export type ChangeQuoteStatusInput = z.infer<typeof changeQuoteStatusInput>;

/**
 * La disponibilité annoncée au client.
 *
 * Les deux dates ensemble, nulles comprises : effacer l'annonce est un geste
 * légitime — un planning bouge, et une date devenue fausse vaut moins que pas
 * de date du tout.
 */
export const announceAvailabilityInput = z.object({
  confirmationId: z.string().optional(),
  quoteId: z.string().uuid(),
  startsOn: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Date attendue au format AAAA-MM-JJ")
    .nullish(),
  endsOn: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Date attendue au format AAAA-MM-JJ")
    .nullish(),
});

export type AnnounceAvailabilityInput = z.infer<typeof announceAvailabilityInput>;
